import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ErrorOutline as CriticalIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ErrorNotification as ErrorNotificationType, ErrorLevel } from '../types/errorNotifications';

interface ErrorNotificationProps {
  notification: ErrorNotificationType;
  onRemove: (id: string) => void;
  index: number;
  total: number;
}

const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  notification,
  onRemove,
  index,
  total
}) => {
  const theme = useTheme();

  const getStyles = (level: ErrorLevel) => {
    switch (level) {
      case 'error':
        return {
          icon: <ErrorIcon />,
          color: theme.palette.error.main,
          bgColor: alpha(theme.palette.error.main, 0.1),
          borderColor: theme.palette.error.main,
        };
      case 'warning':
        return {
          icon: <WarningIcon />,
          color: '#FF690F', // Orange from Tunisian design
          bgColor: alpha('#FF690F', 0.1),
          borderColor: '#FF690F',
        };
      case 'critical':
        return {
          icon: <CriticalIcon />,
          color: '#D51007', // Tunisia flag red
          bgColor: alpha('#D51007', 0.1),
          borderColor: '#D51007',
        };
      case 'info':
        return {
          icon: <InfoIcon />,
          color: '#499FDD', // Sky blue from Tunisian design
          bgColor: alpha('#499FDD', 0.1),
          borderColor: '#499FDD',
        };
      default:
        return {
          icon: <ErrorIcon />,
          color: theme.palette.error.main,
          bgColor: alpha(theme.palette.error.main, 0.1),
          borderColor: theme.palette.error.main,
        };
    }
  };

  const styles = getStyles(notification.level);

  const handleRemove = useCallback(() => {
    onRemove(notification.id);
  }, [notification.id, onRemove]);

  const handleActionClick = useCallback((action: any) => {
    action.onClick();
    if (!notification.persistent) {
      handleRemove();
    }
  }, [handleRemove, notification.persistent]);

  // Calculate position based on index
  const bottomOffset = 16 + (index * 80); // 80px spacing between notifications, stacking upward
  const zIndex = 1500 + (total - index); // Higher z-index for newer notifications

  return (
    <motion.div
      initial={{ opacity: 0, x: 400, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 400, scale: 0.8 }}
      transition={{
        duration: 0.3,
        ease: "easeOut"
      }}
      style={{
        position: 'fixed',
        bottom: bottomOffset,
        right: 16,
        zIndex,
        maxWidth: 400,
        minWidth: 300,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 2,
          borderRadius: 2,
          borderLeft: 4,
          borderColor: styles.borderColor,
          backgroundColor: styles.bgColor,
          backdropFilter: 'blur(8px)',
          boxShadow: theme.shadows[6],
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[8],
          },
        }}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              color: styles.color,
              mt: 0.25,
              '& svg': { fontSize: 20 }
            }}
          >
            {styles.icon}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: styles.color,
                fontWeight: 600,
                mb: 0.5,
                wordBreak: 'break-word'
              }}
            >
              {notification.title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                wordBreak: 'break-word',
                lineHeight: 1.4,
                whiteSpace: 'pre-line',
              }}
            >
              {notification.message}
            </Typography>

            {notification.actions && notification.actions.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {notification.actions.map((action, actionIndex) => (
                  <Button
                    key={actionIndex}
                    size="small"
                    variant={action.variant || 'text'}
                    onClick={() => handleActionClick(action)}
                    sx={{
                      color: styles.color,
                      borderColor: styles.color,
                      '&:hover': {
                        backgroundColor: alpha(styles.color, 0.1),
                        borderColor: styles.color,
                      },
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={handleRemove}
            sx={{
              color: 'text.secondary',
              p: 0.5,
              '&:hover': {
                backgroundColor: alpha(styles.color, 0.1),
                color: styles.color,
              }
            }}
            aria-label="Close notification"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default ErrorNotification;
